import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { cable } from '../../cable.js'
import { t } from '../../i18n.js'
import { generateRequestId } from '../utils/requestId.js'
import ValueBridgeWaterfall from './ValueBridgeWaterfall.jsx'
import {
  EM_DASH,
  annualSeriesCells,
  chartBars,
  changedInputField,
  changedInputItem,
  changedInputSections,
  correspondenceNotices,
  economicSummary,
  formatIsoDate,
  formatChangedInputValue,
  formatMoney,
  formatMoneyMovement,
  formatMovement,
  formatSignedPercent,
  formatWhole,
  groupIdFor,
  groupSections,
  movementTone,
  presentableResult,
  rankedRows,
  scheduleRows,
  presentationRows,
  rowLabel
} from '../value_bridge/format.js'

// EVA-303: the Value Bridge tab. It answers one question — why did this
// property's valuation move between these two saved valuations — and it answers
// it by attribution, not by narration.
//
// Presentation only. Every figure arrives from a ValueBridgeRun computed in a
// job (3 + 2U runs of the full engine is not request work), and this component
// owns order, labels and formatting.
//
// The posture the whole feature takes, and that this file must not soften:
//   * a movement nobody can attribute is a labelled Residual line, never spread
//     silently over the drivers;
//   * a record the resolver could not correspond is reported as exactly that.
//     The model cannot know whether a lease was renewed, surrendered or
//     replaced, so the tab does not say so;
//   * a failed run says what went wrong, as a translated code, and offers to
//     run again. It never shows a partial attribution;
//   * the figures are the output of an explicit Run, not a live derivation. An
//     upstream edit makes them STALE, not provisional — so the tab raises a
//     banner and does NOT quietly refetch behind the reader.
const POLL_INTERVAL = 4000
// An engine run is seconds. Past this the run is not slow, it is lost — a
// Sidekiq restart between enqueue and perform leaves a PENDING row that the
// job's own rescue never sees, and without this the tab polls for ever with
// the Run button disabled, so the pair can never be asked about again.
const POLL_GIVE_UP_MS = 5 * 60 * 1000
const HIGHLIGHT_MS = 1600

// api.md: a CODE, never a message. Anything that is not one — a JSON parse
// failure on the HTML of a session-timeout redirect, a network TypeError —
// becomes the generic line. i18n.js's `t()` answers a miss with the lookup path
// itself, so without this the user reads `errors.Failed to fetch` on the page.
const ERROR_CODE = /^[a-z][a-z0-9_]*$/
const asCode = (value) => (ERROR_CODE.test(String(value || '')) ? value : 'error_fallback')

export default function ValueBridgeTab({
  valuationId,
  stateUrl,
  runUrl,
  csrfToken,
  currency,
  translations
}) {
  const [endpoints, setEndpoints] = useState([])
  const [earlierId, setEarlierId] = useState(null)
  const [run, setRun] = useState(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [pendingScroll, setPendingScroll] = useState(null)
  const [expandedIds, setExpandedIds] = useState(() => new Set())
  const [collapsed, setCollapsed] = useState(() => new Set())
  const [inputsChanged, setInputsChanged] = useState(false)
  const [showAudit, setShowAudit] = useState(false)

  const label = useCallback((key, vars) => t(key, translations, vars || {}), [translations])

  // Monotonic sequence guard, the CashFlowReport shape: a slow fetch must not
  // overwrite the fresher one that overtook it. Here it is not hypothetical —
  // the cable frame, the poll and the run POST can all land within a few
  // milliseconds.
  const requestSeqRef = useRef(0)
  const appliedSeqRef = useRef(0)
  const highlightTimerRef = useRef(null)
  const presentedRunIdRef = useRef(null)

  const pairUrl = useCallback((id) => {
    if (!id) return stateUrl
    const url = new URL(stateUrl, window.location.origin)
    url.searchParams.set('earlier_valuation_id', String(id))
    return url.toString()
  }, [stateUrl])

  const fetchState = useCallback(async (earlier, { spinner = true } = {}) => {
    if (spinner) setLoading(true)
    const seq = (requestSeqRef.current += 1)

    try {
      const res = await fetch(pairUrl(earlier), { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || 'error_fallback')
      if (seq < appliedSeqRef.current) return
      appliedSeqRef.current = seq

      const nextRun = json.data.run || null

      // Initialise disclosure state before presenting a different result. That
      // keeps the first painted frame collapsed instead of briefly exposing
      // every driver until an effect catches up. Polls and cable refetches of
      // the same persisted run deliberately preserve the reader's choices.
      if (nextRun?.result && presentedRunIdRef.current !== nextRun.id) {
        const nextRows = presentationRows(nextRun.result)
        const nextSections = groupSections(nextRows, nextRun.result.groups)
        presentedRunIdRef.current = nextRun.id
        setCollapsed(new Set(
          nextSections
            .filter((section) => section.id !== 'valuation_date_roll_forward')
            .map((section) => section.id)
        ))
        setExpandedIds(new Set())
        setSelectedId(null)
      } else if (!nextRun?.result) {
        presentedRunIdRef.current = null
      }

      setEndpoints(json.data.endpoints || [])
      setRun(nextRun)
      setInputsChanged(false)
      setError(null)
    } catch (e) {
      if (seq < appliedSeqRef.current) return
      // Claim the sequence on this path too. Without it a slow OLDER success
      // landing after a fast newer failure overwrites both the error and the
      // data, and the picker then names one pair while the table describes
      // another.
      appliedSeqRef.current = seq
      setError(asCode(e?.message))
    } finally {
      if (spinner) setLoading(false)
    }
  }, [pairUrl])

  // The default pair: this valuation against the one immediately before it in
  // the property's own history. Deliberately NOT "the last one I can find" —
  // skipping back over an empty period answers a different question.
  useEffect(() => {
    let cancelled = false

    const bootstrap = async () => {
      const res = await fetch(stateUrl, { headers: { Accept: 'application/json' } })
      const json = await res.json().catch(() => null)
      if (cancelled) return
      if (!res.ok || !json?.success) { setError('error_fallback'); setLoading(false); return }

      const options = json.data.endpoints || []
      setEndpoints(options)
      const index = options.findIndex((option) => option.current)
      const previous = index >= 0 ? options[index + 1] : options[1]
      if (previous) {
        setEarlierId(previous.id)
        fetchState(previous.id)
      } else {
        setLoading(false)
      }
    }

    bootstrap().catch(() => { if (!cancelled) { setError('error_fallback'); setLoading(false) } })
    return () => { cancelled = true }
  }, [stateUrl, fetchState])

  const earlierRef = useRef(earlierId)
  useEffect(() => { earlierRef.current = earlierId }, [earlierId])

  // Its own channel, not a ValuationChannel frame: every current subscriber
  // treats any frame there as "go refetch" and ignores the payload, so a
  // value_bridge_done broadcast there would make every open Cash Flow Report
  // and Valuation Summary refetch for nothing.
  useEffect(() => {
    if (!valuationId) return undefined

    const subscription = cable.subscriptions.create(
      { channel: 'ValueBridgeChannel', valuation_id: valuationId },
      {
        received(payload) {
          if (payload?.type !== 'value_bridge_done' && payload?.type !== 'value_bridge_failed') return
          fetchState(earlierRef.current, { spinner: false })
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [valuationId, fetchState])

  // The pulse, on both endpoints, and deliberately NOT through
  // `useValuationPulse`: that hook dims the figures and refetches, which is
  // right for a live derivation and wrong here. A bridge is the output of an
  // explicit Run — an upstream edit does not make its numbers provisional, it
  // makes them describe inputs that no longer exist. So: raise the banner, and
  // leave the decision to run again with the valuer.
  useEffect(() => {
    const ids = [ valuationId, earlierId ].filter(Boolean)
    if (ids.length === 0) return undefined

    const subscriptions = ids.map((id) => cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: id },
      { received() { setInputsChanged(true) } }
    ))

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [valuationId, earlierId])

  // A safety poll while a run is pending, and only then. The cable frame is the
  // mechanism; this is what stops a dropped websocket leaving a person watching
  // a spinner over a run that finished minutes ago. It is a GET — the write is
  // never retried (api.md: nothing dedupes on request_id).
  //
  // `run?.id` is in the dependency list on purpose: without it a second run for
  // the same pair inherits the first one's timer, and its start time with it.
  useEffect(() => {
    if (run?.status !== 'PENDING') return undefined

    const startedAt = Date.now()
    const timer = setInterval(() => {
      if (Date.now() - startedAt > POLL_GIVE_UP_MS) {
        clearInterval(timer)
        setRun((current) => (current?.status === 'PENDING'
          ? { ...current, status: 'FAILED', error: 'run_abandoned' }
          : current))
        return
      }
      fetchState(earlierRef.current, { spinner: false })
    }, POLL_INTERVAL)

    return () => clearInterval(timer)
  }, [run?.id, run?.status, fetchState])

  useEffect(() => () => clearTimeout(highlightTimerRef.current), [])

  const selectEarlier = useCallback((value) => {
    const id = Number(value)
    setEarlierId(id)
    fetchState(id)
  }, [fetchState])

  const startRun = useCallback(async () => {
    if (!earlierId || starting) return
    setStarting(true)
    setError(null)

    try {
      const res = await fetch(runUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Request-Id': generateRequestId()
        },
        body: JSON.stringify({ run: { earlier_valuation_id: earlierId, later_valuation_id: valuationId } })
      })
      const json = await res.json()
      if (!json.success) { setError(asCode(json.error)); return }

      // Read the run back rather than setting it from this response. The job
      // can finish — and its cable frame arrive — before this promise resolves,
      // and assigning PENDING here would overwrite the finished run with a
      // status that is already out of date. `fetchState` carries the sequence
      // guard that decides which answer is the fresher one; this does not.
      await fetchState(earlierId, { spinner: false })
    } catch {
      setError('error_fallback')
    } finally {
      setStarting(false)
    }
  }, [earlierId, valuationId, runUrl, csrfToken, starting, fetchState])

  const result = presentableResult(run)
  const rows = useMemo(() => presentationRows(result), [result])
  const sections = useMemo(() => groupSections(rows, result?.groups), [rows, result])
  const topMovers = useMemo(() => rankedRows(rows, 5), [rows])
  const notices = useMemo(() => correspondenceNotices(result?.correspondence), [result])
  const rowsById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows])
  const bars = useMemo(() => chartBars(result?.waterfall), [result])
  const rollForwardSection = useMemo(
    () => sections.find((section) => section.id === 'valuation_date_roll_forward') || null,
    [sections]
  )
  const collapsibleSections = useMemo(
    () => sections.filter((section) => section.id !== 'valuation_date_roll_forward'),
    [sections]
  )

  const toggleGroup = useCallback((id) => {
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const allGroupsExpanded = collapsibleSections.length > 0 &&
    collapsibleSections.every((section) => !collapsed.has(section.id))

  const toggleAllGroups = useCallback(() => {
    if (allGroupsExpanded) {
      setCollapsed(new Set(collapsibleSections.map((section) => section.id)))
      setExpandedIds(new Set())
    } else {
      setCollapsed(new Set())
    }
  }, [allGroupsExpanded, collapsibleSections])

  // Selecting a bar opens its group and brings the row into view. It does NOT
  // move focus: a keyboard reader stepping along the bars would be thrown out
  // of the chart by their own first selection, and scrollIntoView does not
  // move focus.
  const select = useCallback((id) => {
    setSelectedId(id)

    const group = groupIdFor(rows, id)
    if (group) {
      setCollapsed((current) => {
        if (!current.has(group)) return current
        const next = new Set(current)
        next.delete(group)
        return next
      })
      setPendingScroll(id)
    }

    clearTimeout(highlightTimerRef.current)
    highlightTimerRef.current = setTimeout(() => setSelectedId(null), HIGHLIGHT_MS)
  }, [rows])

  // Expand first, then scroll — in a layout effect, so the row exists and the
  // destination is in view before the browser paints the disclosed analysis.
  // Scrolling inside the click handler lands on the collapsed layout's
  // coordinates and stops short.
  useLayoutEffect(() => {
    if (!pendingScroll) return

    const row = document.querySelector(`[data-row-id="${CSS.escape(pendingScroll)}"]`)
    // This is navigation, not decoration: once the matching card is disclosed,
    // the promised destination must already be in view when selection settles.
    // A smooth scroll leaves an observable interval where the row exists but is
    // still off-screen, and grows arbitrarily long with a tall analysis.
    row?.scrollIntoView({ block: 'center', behavior: 'auto' })
    setPendingScroll(null)
  }, [pendingScroll])

  const nameOf = useCallback((row) => {
    const { key, vars } = rowLabel(row)
    return label(key, vars)
  }, [label])

  const barName = useCallback((bar) => {
    if (bar.id === 'opening') return label('chart.opening')
    if (bar.id === 'closing') return label('chart.closing')
    const row = rowsById.get(bar.id)
    if (row?.kind === 'residual') return label('chart.driver_interaction')
    return nameOf(row || { kind: 'driver', driver_id: bar.id })
  }, [label, nameOf, rowsById])

  const barValue = useCallback(
    (bar) => (bar.kind === 'total' ? formatWhole(bar.amount) : formatMovement(bar.amount)),
    []
  )

  // Label, signed amount and share of the opening — the drawing is aria-hidden,
  // so this label button IS an interactive movement bar as far as assistive
  // technology is concerned. Endpoint totals are deliberately static text.
  const barAria = useCallback((bar) => {
    const row = rowsById.get(bar.id)
    const share = row ? ` (${formatSignedPercent(row.percent_of_opening)})` : ''
    return `${barName(bar)}: ${barValue(bar)} ${currency || ''}${share}`.replace(/\s+/g, ' ').trim()
  }, [barName, barValue, currency, rowsById])

  const currentEndpoint = useMemo(
    () => endpoints.find((endpoint) => endpoint.current) || null,
    [endpoints]
  )
  const earlierEndpoint = useMemo(
    () => endpoints.find((endpoint) => endpoint.id === earlierId) || null,
    [endpoints, earlierId]
  )
  const endpointsFinalised = [earlierEndpoint, currentEndpoint]
    .every((endpoint) => endpoint?.status === 'FINALISED')

  // Only endpoints the server would actually accept. `check_date_order!`
  // refuses an earlier endpoint dated after the later one, and offering a
  // choice that is always refused is a round trip to an error message. `<=`,
  // because a same-date sibling version is a legitimate comparison.
  const earlierOptions = useMemo(() => {
    const boundary = currentEndpoint?.valuation_date
    return endpoints.filter((endpoint) => !endpoint.current &&
      (!boundary || !endpoint.valuation_date || endpoint.valuation_date <= boundary))
  }, [endpoints, currentEndpoint])

  const endpointLabel = useCallback((endpoint) => label('endpoint_option', {
    version: endpoint.version,
    date: formatIsoDate(endpoint.valuation_date),
    status: label(`statuses.${endpoint.status}`)
  }), [label])

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{label('loading')}</span>
        </div>
      </div>
    )
  }

  const pending = run?.status === 'PENDING'
  // Defence only — the job writes status and result in one update. A blank tab
  // that says nothing is the worst of the possible answers, so a run that
  // claims to be complete but carries nothing is treated as a failure.
  // A stale schema is deliberately withheld by the server: it needs a rerun,
  // not a red failure claiming the calculation returned nothing. A current
  // COMPLETE run without a result remains an integrity failure.
  const resultMissing = run?.status === 'COMPLETE' && !run.result && !run.stale
  const failure = run?.status === 'FAILED' || resultMissing
  const headline = result?.headline
  const hasRoundedOpening = headline?.opening_rounded != null
  const largestMover = topMovers[0]
  const largestTone = movementTone(largestMover?.amount)

  return (
    <div className="value-bridge-tab">
      {error && <div className="alert alert-danger mb-3">{label(`errors.${error}`)}</div>}

      <div className="card mb-3">
        <div className="card-body vb-endpoints">
          <div className="vb-endpoint">
            <label className="form-label" htmlFor="vb-earlier">{label('endpoints.earlier')}</label>
            <select
              id="vb-earlier"
              className="form-select"
              value={earlierId || ''}
              onChange={(event) => selectEarlier(event.target.value)}
            >
              <option value="" disabled>{label('endpoints.choose')}</option>
              {earlierOptions.map((endpoint) => (
                <option key={endpoint.id} value={endpoint.id}>{endpointLabel(endpoint)}</option>
              ))}
            </select>
          </div>

          {/* Fixed, and not a picker. `show` scopes the run lookup to the
              valuation whose tab this is, so a run recorded against some other
              later endpoint could never be read back. Comparing a different
              pair means opening that valuation's own tab — the header's version
              dropdown is one click away. */}
          <div className="vb-endpoint">
            <span className="form-label d-block">{label('endpoints.later')}</span>
            <p className="form-control-plaintext mb-0" data-testid="vb-later">
              {currentEndpoint ? endpointLabel(currentEndpoint) : EM_DASH}
            </p>
          </div>

          <div className="vb-endpoint vb-endpoint--action">
            <button
              type="button"
              className="btn btn-primary"
              onClick={startRun}
              disabled={!earlierId || pending || starting}
              data-testid="vb-run"
            >
              {pending || starting ? label('running') : label('run')}
            </button>
          </div>
        </div>
      </div>

      {pending && (
        <div className="alert alert-info d-flex align-items-center gap-2 mb-3" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          {label('pending')}
        </div>
      )}

      {failure && (
        <div className="alert alert-danger mb-3" data-testid="vb-failure">
          {label(`errors.${run.error || 'error_fallback'}`)}
        </div>
      )}

      {!run && !pending && (
        <div className="card mb-3">
          <div className="card-body text-muted text-center py-5">{label('empty')}</div>
        </div>
      )}

      {(run?.stale || inputsChanged) && (
        <div className="alert alert-warning mb-3" data-testid="vb-stale">{label('stale')}</div>
      )}

      {headline && (
        <>
          <div className="card mb-3 vb-headline-card" data-testid="vb-headline">
            <div className="card-body p-0">
              <div className="vb-headline">
                <Endpoint
                  kind="opening"
                  label={label('headline.opening')}
                  value={formatWhole(headline.opening_rounded ?? headline.opening_raw)}
                  endpoint={earlierEndpoint}
                  translate={label}
                />
                <div
                  className={`vb-headline-section vb-headline-movement vb-headline-movement--${headline.direction}`}
                  data-testid="vb-headline-movement"
                >
                  <div className="vb-headline-label text-muted text-uppercase">
                    {label('headline.movement')}
                  </div>
                  {/* The rounded movement, to match the rounded endpoints
                      beside it. Printing the raw one between them would leave
                      three figures where the outer two minus the middle one is
                      the rounding plug. */}
                  <div className="vb-headline-value" data-testid="vb-movement">
                    {formatMovement(headline.movement_rounded ?? headline.movement_raw)}
                  </div>
                  <div className="vb-headline-support">
                    {formatSignedPercent(headline.movement_percent)}
                  </div>
                </div>
                <Endpoint
                  kind="closing"
                  label={label('headline.closing')}
                  value={formatWhole(headline.closing_rounded ?? headline.closing_raw)}
                  endpoint={currentEndpoint}
                  translate={label}
                />
              </div>

              {!endpointsFinalised && (
                <div className="vb-provenance" data-testid="vb-draft-warning" role="note">
                  <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                  <span>{label('unofficial')}</span>
                </div>
              )}
            </div>
          </div>

          <div className="card mb-3 vb-chart-card" data-testid="vb-chart-card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">{label('chart.title')}</h5>
              {largestMover && (
                <span
                  className={`vb-largest-driver vb-largest-driver--${largestTone}`}
                  data-testid="vb-largest-driver"
                >
                  <i className="bi bi-bar-chart-fill" aria-hidden="true" />
                  <span className="vb-largest-driver-copy">
                    <span>{label('chart.largest_label')}</span>{' '}
                    <span className="vb-largest-driver-name">{nameOf(largestMover)}</span>
                  </span>
                </span>
              )}
            </div>
            <div className="card-body">
              <ValueBridgeWaterfall
                bars={bars}
                labelFor={barName}
                formatValue={barValue}
                ariaLabelFor={barAria}
                selectedId={selectedId}
                onSelect={select}
                emptyLabel={label('chart.unavailable')}
                axisNote={label('chart.axis_note')}
              />
            </div>
          </div>

          <div className="card mb-3 vb-analysis-card">
            <div className="card-header d-flex justify-content-between align-items-center gap-3">
              <h5 className="card-title mb-0">{label('table.title')}</h5>
              {collapsibleSections.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={toggleAllGroups}
                >
                  {label(allGroupsExpanded ? 'table.collapse_all' : 'table.expand_all')}
                </button>
              )}
            </div>
            <div className="card-body vb-analysis" data-testid="vb-driver-analysis">
              <div className="vb-analysis-list vb-table">
                <AnalysisAnchor
                  kind="opening"
                  title={label(hasRoundedOpening ? 'table.opening_anchor' : 'table.opening_raw_anchor')}
                  support={label('table.opening_support')}
                  value={formatMoney(headline.opening_rounded ?? headline.opening_raw, currency)}
                />

                {rollForwardSection && (
                  <RollForwardRow
                    section={rollForwardSection}
                    title={label(`groups.${rollForwardSection.id}`)}
                    rowName={nameOf(rollForwardSection.rows[0])}
                    selectedId={selectedId}
                    currency={currency}
                  />
                )}

                {collapsibleSections.map((section) => (
                  <AnalysisGroup
                    key={section.id}
                    section={section}
                    title={label(`groups.${section.id}`)}
                    collapsed={collapsed.has(section.id)}
                    selectedId={selectedId}
                    expandedIds={expandedIds}
                    onToggleGroup={() => toggleGroup(section.id)}
                    onToggleRow={(rowId) => setExpandedIds((current) => {
                      const next = new Set(current)
                      if (next.has(rowId)) next.delete(rowId)
                      else next.add(rowId)
                      return next
                    })}
                    nameOf={nameOf}
                    label={label}
                    currency={currency}
                  />
                ))}

                <AnalysisAnchor
                  kind="closing"
                  title={label('table.closing_anchor')}
                  support={label('table.closing_support')}
                  value={formatMoney(headline.closing_rounded ?? headline.closing_raw, currency)}
                />
              </div>
            </div>
          </div>

          {notices.length > 0 && (
            <div className="alert alert-secondary mb-3">
              <p className="mb-1 fw-semibold">{label('correspondence.title')}</p>
              <ul className="mb-0 ps-3">
                {notices.map((notice) => (
                  <li key={`${notice.side}-${notice.model}`}>
                    {label(`correspondence.${notice.side}`, {
                      count: notice.count,
                      model: label(`models.${notice.model}`, { count: notice.count })
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="card">
            <div className="card-body py-2">
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                aria-expanded={showAudit}
                onClick={() => setShowAudit((open) => !open)}
              >
                {label(showAudit ? 'audit.hide' : 'audit.show')}
              </button>
              {showAudit && (
                <dl className="vb-audit mb-0 mt-2">
                  <Stat label={label('audit.engine_runs')} value={result.statistics?.engine_runs} />
                  <Stat label={label('audit.requested')} value={result.statistics?.requested} />
                  <Stat label={label('audit.deduplicated')} value={result.statistics?.deduplicated} />
                  {/* Formatted by the server, inside the user's own EVA
                      timezone. `toLocaleString` would use the browser's, so a
                      user whose laptop disagrees with their EVA profile would
                      read two different times for one run. */}
                  <Stat label={label('audit.completed_at')} value={run.completed_at_label} />
                </dl>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Endpoint({ kind, label, value, endpoint, translate }) {
  const status = endpoint?.status || 'DRAFT'
  const statusClass = status === 'FINALISED' ? 'final' : 'draft'

  return (
    <div
      className={`vb-headline-section vb-headline-endpoint vb-headline-endpoint--${kind}`}
      data-testid={`vb-headline-${kind}`}
    >
      <div className="vb-headline-label text-muted text-uppercase">{label}</div>
      <div className="vb-headline-value">{value}</div>
      {endpoint && (
        <div className="vb-headline-metadata">
          <span>
            {translate('headline.endpoint_metadata', {
              date: formatIsoDate(endpoint.valuation_date),
              version: endpoint.version
            })}
          </span>
          <span className={`vb-headline-status vb-headline-status--${statusClass}`}>
            {translate(`statuses.${status}`)}
          </span>
        </div>
      )}
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="vb-audit-item">
      <dt className="text-muted">{label}</dt>
      <dd className="mb-0">{value ?? EM_DASH}</dd>
    </div>
  )
}

function AnalysisAnchor({ kind, title, support, value }) {
  return (
    <div className={`vb-analysis-anchor vb-analysis-anchor--${kind}`} data-analysis-order>
      <div>
        <div className="vb-analysis-title">{title}</div>
        <div className="vb-analysis-support">{support}</div>
      </div>
      <div className="vb-analysis-value">{value}</div>
    </div>
  )
}

function RollForwardRow({ section, title, rowName, selectedId, currency }) {
  const row = section.rows[0]
  const tone = movementTone(section.amount)

  return (
    <div
      className={`vb-roll-forward${row?.id === selectedId ? ' vb-row--selected' : ''}`}
      data-analysis-order
      data-testid="vb-roll-forward"
      data-row-id={row?.id}
    >
      <i className="bi bi-clock-history" aria-hidden="true" />
      <div className="vb-analysis-copy">
        <div className="vb-analysis-title">{title}</div>
        <div className="vb-analysis-support">{rowName}</div>
      </div>
      <div className={`vb-analysis-figures vb-analysis-figures--${tone}`}>
        <div className="vb-analysis-value">{formatMoneyMovement(section.amount, currency)}</div>
        <div className="vb-analysis-percent">{formatSignedPercent(section.percent_of_opening)}</div>
      </div>
    </div>
  )
}

function AnalysisGroup({
  section,
  title,
  collapsed,
  selectedId,
  expandedIds,
  onToggleGroup,
  onToggleRow,
  nameOf,
  label,
  currency
}) {
  const tone = movementTone(section.amount)
  const countKey = section.id === 'reconciliation' ? 'table.line_count' : 'table.driver_count'

  return (
    <div className="vb-analysis-group" data-analysis-order data-group-id={section.id}>
      <button
        type="button"
        className="vb-group-toggle"
        aria-expanded={!collapsed}
        aria-controls={`vb-group-${section.id}`}
        onClick={onToggleGroup}
      >
        <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`} aria-hidden="true" />
        <span className="vb-analysis-copy">
          <span className="vb-analysis-title">{title}</span>
          <span className="vb-analysis-support">{label(countKey, { count: section.row_count })}</span>
        </span>
        <span className={`vb-analysis-figures vb-analysis-figures--${tone}`}>
          <span className="vb-analysis-value">{formatMoneyMovement(section.amount, currency)}</span>
          <span className="vb-analysis-percent vb-group-percent">
            {formatSignedPercent(section.percent_of_opening)}
          </span>
        </span>
      </button>

      {!collapsed && (
        <div className="vb-group-rows" id={`vb-group-${section.id}`}>
          {section.rows.map((row) => (
            <DriverRow
              key={row.id}
              row={row}
              name={nameOf(row)}
              selected={row.id === selectedId}
              expanded={expandedIds.has(row.id)}
              onToggle={() => onToggleRow(row.id)}
              label={label}
              currency={currency}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DriverRow({ row, name, selected, expanded, onToggle, label, currency }) {
  const inputs = row.changed_inputs ?? row.changedInputs ?? []
  const inputCount = row.changed_inputs_count ?? row.changedInputsCount ?? inputs.length
  const inputsTruncated = row.changed_inputs_truncated ?? row.changedInputsTruncated ?? false
  const summary = economicSummary(row.summary, label)
  const expandable = inputs.length > 0
  const detailId = `vb-row-inputs-${row.id}`
  const classes = [
    'vb-row',
    `vb-row--${row.kind}`,
    selected ? 'vb-row--selected' : ''
  ].filter(Boolean).join(' ')

  return (
    <div className={classes} data-row-id={row.id}>
      {expandable ? (
        <button
          type="button"
          className="vb-row-main vb-row-toggle"
          aria-expanded={expanded}
          aria-controls={detailId}
          onClick={onToggle}
          data-testid="vb-driver-toggle"
        >
          <i className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} aria-hidden="true" />
          <DriverCopy row={row} name={name} inputCount={inputCount} summary={summary} label={label} />
          <DriverFigures row={row} currency={currency} />
        </button>
      ) : (
        <div className="vb-row-main vb-row-main--static">
          <span className="vb-row-spacer" aria-hidden="true" />
          <DriverCopy row={row} name={name} inputCount={inputCount} summary={summary} label={label} />
          <DriverFigures row={row} currency={currency} />
        </div>
      )}
      {expanded && (
        <div className="vb-inputs" id={detailId}>
          <h4 className="vb-inputs-title">{label('inputs.changed_inputs')}</h4>
          <ChangedInputDetails inputs={inputs} label={label} currency={currency} />
          {inputsTruncated && (
            <p className="vb-inputs-truncated mb-0">
              {label('table.inputs_truncated', { shown: inputs.length, total: inputCount })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function ChangedInputDetails({ inputs, label, currency }) {
  const { annual, schedules, values, showField } = changedInputSections(inputs)

  return (
    <>
      {annual.map((input, index) => (
        <AnnualSeriesTable
          key={`${input.model}-${input.column}-${input.item_label || ''}-${index}`}
          input={input}
          label={label}
        />
      ))}
      {schedules.map((input, index) => (
        <ScheduleTable
          key={`${input.model}-${input.column}-${input.item_label || ''}-${index}`}
          input={input}
          label={label}
          currency={currency}
        />
      ))}
      {values.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm vb-input-table mb-0">
            <thead>
              <tr>
                <th>{label('inputs.item')}</th>
                {showField && <th>{label('inputs.field')}</th>}
                <th className="text-end">{label('inputs.earlier')}</th>
                <th className="text-end">{label('inputs.later')}</th>
              </tr>
            </thead>
            <tbody>
              {values.map((input, index) => (
                <tr key={`${input.model}-${input.column}-${input.item_label || ''}-${index}`}>
                  <th scope="row">{changedInputItem(input, label)}</th>
                  {showField && <td>{changedInputField(input, label)}</td>}
                  <td className="text-end vb-input-value">
                    {formatChangedInputValue(input, 'earlier', currency, label)}
                  </td>
                  <td className="text-end vb-input-value">
                    {formatChangedInputValue(input, 'later', currency, label)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function ScheduleTable({ input, label, currency }) {
  const rows = scheduleRows(input, currency, label)

  return (
    <div className="table-responsive vb-schedule">
      <table className="table table-sm vb-input-table mb-0">
        <caption>{changedInputItem(input, label)}</caption>
        <thead>
          <tr>
            <th>{label('inputs.schedule.title')}</th>
            <th className="text-end">{label('inputs.earlier')}</th>
            <th className="text-end">{label('inputs.later')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.label}-${index}`}>
              <th scope="row">{row.label}</th>
              <td className="text-end vb-input-value">{row.earlier}</td>
              <td className="text-end vb-input-value">{row.later}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AnnualSeriesTable({ input, label }) {
  const earlier = annualSeriesCells(input, 'earlier')
  const later = annualSeriesCells(input, 'later')
  const years = Math.max(earlier.length, later.length)

  return (
    <div className="table-responsive vb-annual-series">
      <table className="table table-sm vb-input-table mb-0">
        <caption>{changedInputItem(input, label)}</caption>
        <thead>
          <tr>
            <th>{label('inputs.curve')}</th>
            {Array.from({ length: years }, (_, index) => (
              <th className="text-end" key={index}>{label('inputs.year', { year: index + 1 })}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[ [ 'earlier', earlier ], [ 'later', later ] ].map(([ side, cells ]) => (
            <tr key={side}>
              <th scope="row">{label(`inputs.${side}`)}</th>
              {cells.map((cell, index) => (
                <td className="text-end vb-input-value" key={index}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DriverCopy({ row, name, inputCount, summary, label }) {
  return (
    <span className="vb-row-copy">
      <span className="vb-row-name">
        {name}
        {row.band_changed && (
          <span className="badge text-bg-warning ms-2">{label('table.band_changed')}</span>
        )}
      </span>
      {(inputCount > 0 || summary) && (
        <span className="vb-row-summary">
          {inputCount > 0 && label('table.changed_inputs', { count: inputCount })}
          {inputCount > 0 && summary && <> · </>}
          {summary}
        </span>
      )}
    </span>
  )
}

function DriverFigures({ row, currency }) {
  return (
    <span className={`vb-row-figures vb-row-figures--${movementTone(row.amount)}`}>
      <span className="vb-row-amount">{formatMoneyMovement(row.amount, currency)}</span>
      <span className="vb-row-percent">{formatSignedPercent(row.percent_of_opening)}</span>
    </span>
  )
}
